/* iter55g15 — Editor AI actions hook.
 *
 * Wraps the three "small" Claude actions available from the editor
 * header — Continue, Tone-shift, and Accept-suggestion — together with
 * the shared `busy`, `toneBusy`, and `suggestion` state. Extracted
 * from EditorPanel.jsx to keep the panel body focused on composition.
 *
 * ⚠️  This hook does NOT touch the DOM. All content mutation goes
 * through `chaptersApi.update` + `setChapters`, which is safe. The
 * manuscript body owns the sensitive contentEditable sync layer; we
 * only supply new content strings.
 */
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { generateApi, chaptersApi } from '@/lib/api';

export function useEditorAiActions({
  project, chapter, paragraphs, isDemo, recordLock,
  setChapters, refreshTokens, reportSave,
}) {
  const [busy, setBusy]           = useState(false);
  const [toneBusy, setToneBusy]   = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  const askContinue = useCallback(async () => {
    if (!project || !chapter) return;
    if (isDemo) { recordLock?.('Continue with Claude'); return; }
    setBusy(true);
    setSuggestion(null);
    try {
      const last = (paragraphs || []).slice(-3).map((p) => p.text).join('\n\n');
      const res = await generateApi.cont({
        project_id: project.id,
        chapter_id: chapter.id,
        selected_text: last,
        prompt: 'Continue',
      });
      // Continue appends new prose to the chapter tail.
      setSuggestion({ text: res.text, mode: 'append' });
      refreshTokens?.();
    } catch {
      toast.error('Continue failed.');
    } finally {
      setBusy(false);
    }
  }, [project, chapter, paragraphs, isDemo, recordLock, refreshTokens]);

  const askToneShift = useCallback(async (tone) => {
    if (!project || !chapter) return;
    if (isDemo) { recordLock?.('Try again with different tone'); return; }
    setToneBusy(tone);
    setSuggestion(null);
    try {
      const last = (paragraphs || []).slice(-3).map((p) => p.text).join('\n\n');
      const res = await generateApi.toneShift({
        project_id: project.id,
        chapter_id: chapter.id,
        tone,
        selected_text: last || undefined,
      });
      // Tone shift REWRITES the same passage; store the source text as
      // `anchor` so acceptSuggestion can swap it out without duplicating.
      setSuggestion({ text: res.text, mode: 'replace-tail', anchor: last });
      refreshTokens?.();
    } catch (err) {
      toast.error(`Tone shift failed: ${err?.response?.data?.detail || err.message || 'unknown'}`);
    } finally {
      setToneBusy(null);
    }
  }, [project, chapter, paragraphs, isDemo, recordLock, refreshTokens]);

  const acceptSuggestion = useCallback(async () => {
    if (!suggestion || !chapter) return;
    const cur = chapter.content || '';
    const mode = suggestion.mode || 'append';
    const newText = (suggestion.text || '').trim();
    let newContent;
    // iter-p3-x — Track which paragraph the accepted suggestion
    // affected so the manuscript-tint can be per-paragraph exact.
    let anchoredParagraphId = null;
    if (mode === 'replace-tail' && suggestion.anchor) {
      const anchor = suggestion.anchor;
      const at = cur.lastIndexOf(anchor);
      if (at >= 0) {
        newContent = (cur.slice(0, at) + newText + cur.slice(at + anchor.length)).trim();
      } else {
        // Anchor no longer present — writer edited since the request
        // fired. Fall back to appending so we never LOSE the suggestion.
        newContent = `${cur}\n\n${newText}`.trim();
      }
      // Best-effort: last paragraph is what got replaced.
      const paras = (paragraphs || []);
      anchoredParagraphId = paras.length ? paras[paras.length - 1]?.id || null : null;
    } else {
      newContent = `${cur}\n\n${newText}`.trim();
    }
    try {
      const doAccept = async () => {
        await chaptersApi.update(chapter.id, { content: newContent });
        const updated = await chaptersApi.get(chapter.id);
        setChapters?.((prev) => prev.map((c) => (c.id === chapter.id
          ? { ...c, content: updated.content, words: updated.words }
          : c)));
        return updated;
      };
      if (reportSave) await reportSave(doAccept(), 'ai-accept');
      else await doAccept();
      // iter-p3-x — Provenance ledger with paragraph anchor.
      try {
        const axios = (await import('axios')).default;
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${project.id}/editing-record`,
          {
            action:         'ai.suggest.accept',
            chapter_id:     chapter.id,
            paragraph_id:   anchoredParagraphId,
            ai_assisted:    true,
            token_count:    Math.max(1, Math.floor(newText.length / 4)),
            prompt_summary: `accept-${mode}`,
          },
          { withCredentials: true, timeout: 8000 },
        );
      } catch { /* provenance is advisory */ }
      toast.success(mode === 'replace-tail' ? 'Passage replaced.' : 'Suggestion inserted.');
      setSuggestion(null);
    } catch {
      if (!reportSave) toast.error('Could not save.');
    }
  }, [suggestion, chapter, setChapters, reportSave, paragraphs, project]);

  const rejectSuggestion = useCallback(() => setSuggestion(null), []);

  return {
    busy, toneBusy, suggestion,
    askContinue, askToneShift, acceptSuggestion, rejectSuggestion,
  };
}
